import axiosInstance from '../auth/axiosInstance';

export const parseFrameworkFileNames = (stored) => {
  if (!stored || typeof stored !== 'string') return [];
  return stored
    .split('|')
    .map((name) => name.trim())
    .filter(Boolean);
};

export const parseFrameworkUrls = (stored) => {
  if (Array.isArray(stored)) {
    return stored.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (!stored || typeof stored !== 'string') return [];
  return stored
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const displayFrameworkFileName = (fileName) => {
  if (!fileName) return '';
  return fileName.replace(/_[0-9a-f]{32}(\.[^.]+)$/i, '$1');
};

export const getFrameworks = async (pageNumber = 1, pageSize = 10, sort = 'name-asc', search = '') => {
  const { data: result } = await axiosInstance.get('/Framework/GetFrameworks', {
    params: { pageNumber, pageSize, sort, search },
  });

  if (
    result?.data &&
    Array.isArray(result.data.items) &&
    typeof result.data.totalCount === 'number'
  ) {
    const { items, totalCount, pageNumber: returnedPage, pageSize: returnedSize } = result.data;
    return {
      items,
      totalCount,
      pageNumber: returnedPage,
      pageSize: returnedSize,
    };
  }

  return { items: [], totalCount: 0, pageNumber, pageSize };
};

export const normalizeSourceType = (row) => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(row.id ?? row.Id);
  const name = String(row.name ?? row.Name ?? '').trim();
  if (!Number.isFinite(id) || id <= 0 || !name) return null;
  const isActiveRaw = row.isActive ?? row.IsActive;
  const isActive = isActiveRaw === undefined || isActiveRaw === null ? true : Boolean(isActiveRaw);
  return { id, name, isActive };
};

export const getSourceTypes = async (search = '', activeOnly = true) => {
  const params: Record<string, string | boolean> = { activeOnly };
  if (search) params.search = search;
  const { data: result } = await axiosInstance.get('/Framework/GetSourceTypes', {
    params,
  });

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to load source types');
  }

  const raw = result?.data;
  const list = Array.isArray(raw) ? raw : [];
  return list.map(normalizeSourceType).filter(Boolean);
};

export const addOrUpdateSourceType = async ({
  id = 0,
  name,
  isActive = true,
}) => {
  const { data: result } = await axiosInstance.post('/Framework/AddOrUpdateSourceType', {
    id,
    name: String(name || '').trim(),
    isActive,
  });

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to save source type');
  }

  const raw = result?.data;
  return normalizeSourceType(raw) ?? raw;
};

export const deleteSourceType = async (id) => {
  const { data: result } = await axiosInstance.post(`/Framework/DeleteSourceType/${id}`);

  const code = result?.code;
  const failed =
    code === 403 ||
    code === 'Forbidden' ||
    (typeof code === 'number' && code >= 400) ||
    result?.data === false;

  if (failed) {
    throw new Error(result?.message || 'Failed to delete source type');
  }

  return result;
};

export const addOrUpdateFramework = async ({
  id = 0,
  sourceFrameworkName,
  referenceCompanyName = '',
  description = '',
  frameworkType = '',
  sourceTypeId = null,
  urls = [],
  keepFiles = [],
  files = [],
}) => {
  const formData = new FormData();
  formData.append('Id', String(id ?? 0));
  formData.append('SourceFrameworkName', sourceFrameworkName?.trim() ?? '');
  formData.append('ReferenceCompanyName', referenceCompanyName?.trim() ?? '');
  formData.append('Description', description?.trim() ?? '');
  formData.append('FrameworkType', frameworkType?.trim() ?? '');
  if (sourceTypeId != null && Number(sourceTypeId) > 0) {
    formData.append('SourceTypeId', String(sourceTypeId));
  }

  parseFrameworkUrls(urls).forEach((url) => {
    formData.append('Urls', url);
  });

  keepFiles.forEach((name) => {
    if (name) formData.append('keepFiles', name);
  });

  files.forEach((file) => {
    if (file instanceof File) {
      formData.append('files', file);
    }
  });

  const { data: result } = await axiosInstance.post(
    '/Framework/AddOrUpdateFramework',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to save framework');
  }

  return result?.data ?? result;
};

export const deleteFramework = async (id) => {
  const { data: result } = await axiosInstance.post(`/Framework/DeleteFramework/${id}`);

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to delete framework');
  }

  return result;
};

const readBlobResponse = async (response) => {
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    throw new Error(text || 'Failed to load framework file');
  }
  return { blob: response.data, contentType };
};

export const fetchFrameworkBlob = async (id, fileName) => {
  const response = await axiosInstance.get(`/Framework/DownloadFramework/${id}`, {
    params: fileName ? { fileName } : undefined,
    responseType: 'blob',
  });
  return readBlobResponse(response);
};

export const downloadFramework = async (id, fileName) => {
  const { blob } = await fetchFrameworkBlob(id, fileName);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = displayFrameworkFileName(fileName) || fileName || 'framework';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};

export const viewFramework = async (id, fileName) => {
  const { blob, contentType } = await fetchFrameworkBlob(id, fileName);
  const typedBlob = contentType ? new Blob([blob], { type: contentType }) : blob;
  const blobUrl = URL.createObjectURL(typedBlob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
};
